import type { RustPermissionOpTypeValue } from '~/api/models';
import type { RustPermissionOpInput } from '~/api/services/client/rustPermissions';

export const STEAM_ID64_REGEX = /^7656119\d{10}$/;

export const isValidSteamId64 = (value: string): boolean => STEAM_ID64_REGEX.test(value);

const STEAM_ID64_BASE = 76561197960265728n;

/**
 * Accepts what players actually paste and resolves it to a SteamID64:
 * a SteamID64, a legacy SteamID (STEAM_X:Y:Z), an [U:1:accountid], or a
 * steamcommunity.com/profiles/<id64> link. Returns null when nothing usable
 * is found (vanity /id/ URLs need a Steam API lookup, so they don't parse).
 */
export const parseSteamId = (input: string): string | null => {
    const value = input.trim();
    if (value === '') return null;

    if (STEAM_ID64_REGEX.test(value)) return value;

    const legacy = value.match(/^STEAM_[0-5]:([01]):(\d+)$/i);
    if (legacy) {
        return (STEAM_ID64_BASE + BigInt(legacy[2]) * 2n + BigInt(legacy[1])).toString();
    }

    const steam3 = value.match(/^\[?U:1:(\d+)\]?$/i);
    if (steam3) {
        return (STEAM_ID64_BASE + BigInt(steam3[1])).toString();
    }

    const url = value.match(/steamcommunity\.com\/profiles\/(7656119\d{10})/i);
    if (url) return url[1];

    return null;
};

/** Plugin prefix of a permission string: "kits.admin" -> "kits". */
export const permissionPrefix = (permission: string): string => {
    const dot = permission.indexOf('.');
    return dot === -1 ? permission : permission.slice(0, dot);
};

/**
 * Supersede key for an op. MUST mirror RustPermissionOpType::targetKey() on
 * the backend: grant/revoke (and add/remove) pairs share a key so the newest
 * staged or queued change per target wins.
 */
export const targetKeyFor = (op: RustPermissionOpTypeValue, payload: Omit<RustPermissionOpInput, 'op'>): string => {
    switch (op) {
        case 'group.create':
        case 'group.delete':
            return `g:${payload.group}`;
        case 'group.set_title':
            return `g:${payload.group}:title`;
        case 'group.set_rank':
            return `g:${payload.group}:rank`;
        case 'group.set_parent':
            return `g:${payload.group}:parent`;
        case 'group.grant':
        case 'group.revoke':
            return `gp:${payload.group}:${payload.permission}`;
        case 'user.add_group':
        case 'user.remove_group':
            return `ug:${payload.steam_id}:${payload.group}`;
        case 'user.grant':
        case 'user.revoke':
            return `up:${payload.steam_id}:${payload.permission}`;
        case 'user.set_auth_level':
            return `ua:${payload.steam_id}`;
    }
};

export interface PluginSection {
    plugin: string;
    permissions: string[];
}

/**
 * Registered permissions plus any permission that only exists as a grant
 * (plugin unloaded, wildcard leftovers), grouped into sorted plugin sections
 * for the matrix rows.
 */
export const buildPluginSections = (registered: Record<string, string[]>, grantedElsewhere: Iterable<string>): PluginSection[] => {
    const byPlugin = new Map<string, Set<string>>();

    for (const [plugin, permissions] of Object.entries(registered)) {
        byPlugin.set(plugin, new Set(permissions));
    }

    for (const permission of grantedElsewhere) {
        const plugin = permissionPrefix(permission);
        if (!byPlugin.has(plugin)) byPlugin.set(plugin, new Set());
        byPlugin.get(plugin)!.add(permission);
    }

    return [...byPlugin.entries()]
        .map(([plugin, permissions]) => ({ plugin, permissions: [...permissions].sort() }))
        .sort((a, b) => a.plugin.localeCompare(b.plugin));
};

export interface OpDescription {
    path: string;
    params: Record<string, string | number>;
}

/** Human sentence for a change, as an i18n key + params. */
export const describeOp = (op: RustPermissionOpTypeValue, payload: Record<string, any>): OpDescription => {
    const base = 'server.rust_permissions.staged.descriptions';
    const user = payload.nickname || payload.steam_id || '';
    const params = {
        group: payload.group ?? '',
        permission: payload.permission ?? '',
        title: payload.title ?? '',
        rank: payload.rank ?? 0,
        parent: payload.parent ?? '',
        user,
    };

    switch (op) {
        case 'group.create':
            return { path: `${base}.group_create`, params };
        case 'group.delete':
            return { path: `${base}.group_delete`, params };
        case 'group.set_title':
            return { path: `${base}.group_set_title`, params };
        case 'group.set_rank':
            return { path: `${base}.group_set_rank`, params };
        case 'group.set_parent':
            return { path: payload.parent ? `${base}.group_set_parent` : `${base}.group_clear_parent`, params };
        case 'group.grant':
            return { path: `${base}.group_grant`, params };
        case 'group.revoke':
            return { path: `${base}.group_revoke`, params };
        case 'user.add_group':
            return { path: `${base}.user_add_group`, params };
        case 'user.remove_group':
            return { path: `${base}.user_remove_group`, params };
        case 'user.grant':
            return { path: `${base}.user_grant`, params };
        case 'user.revoke':
            return { path: `${base}.user_revoke`, params };
        case 'user.set_auth_level':
            switch (payload.auth_level ?? 0) {
                case 2:
                    return { path: `${base}.auth_owner`, params };
                case 1:
                    return { path: `${base}.auth_moderator`, params };
                default:
                    return { path: `${base}.auth_none`, params };
            }
    }
};

/** Mirrors the backend payload rule, wildcards included. */
export const PERMISSION_REGEX = /^[a-z0-9_.\-*]{1,191}$/;

export const normalizeGroupName = (name: string): string => name.trim().toLowerCase();

export const GROUP_NAME_REGEX = /^[a-z0-9_-]{1,64}$/;

export const isValidGroupName = (name: string): boolean => GROUP_NAME_REGEX.test(name);
